"use client";

import { useState, useRef, useEffect } from "react";
import { motion } from "@/lib/motion";
import Link from "next/link";
import { useFormik } from "formik";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Send } from "@/components/icons";
import { fieldTouchedError } from "@/lib/form-helpers";
import {
  STATUS_URL,
  HELP_URL,
  EMAIL,
  SUPPORT_CATEGORIES,
  CONTACT_SALES_URL,
} from "@/lib/constants";
import { contactSupportSchema } from "@/lib/form-schemas";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { ContactPage } from "@/components/ui/contact-page";
import { FormError } from "@/components/ui/form-error";
import { FormSuccessPage } from "@/components/ui/form-success";
import { brandActionClasses } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ContactSupportSection() {
  const [submitted, setSubmitted] = useState(false);
  const prefersLight = usePrefersLight();
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!categoryOpen) return;
    function close(e: MouseEvent) {
      if (!categoryRef.current?.contains(e.target as Node)) setCategoryOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setCategoryOpen(false);
        document.getElementById("support-category-trigger")?.focus();
      }
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [categoryOpen]);

  const formik = useFormik({
    initialValues: { name: "", email: "", category: "", message: "" },
    validationSchema: contactSupportSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const body = new FormData();
        body.append("name", values.name);
        body.append("email", values.email);
        body.append("category", values.category);
        body.append("message", values.message);

        const res = await fetch("/api/contact/support", { method: "POST", body });
        if (!res.ok) throw new Error("error");
        toast.success("Message sent!", {
          description: "We'll get back to you as soon as possible.",
        });
        setSubmitted(true);
      } catch {
        toast.error("Something went wrong", {
          description: `Please try again or email ${EMAIL.support}`,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const selectedCategory = SUPPORT_CATEGORIES.find((c) => c.value === formik.values.category);

  if (submitted) {
    return (
      <FormSuccessPage
        title="Message sent!"
        description="Our support team will get back to you as soon as possible."
      />
    );
  }

  return (
    <ContactPage
      submitted={submitted}
      leftColumn={
        <>
          <h1 className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Contact support
          </h1>
          <p className="text-muted-foreground mb-8 max-w-xs text-sm leading-relaxed">
            We are here to help. Ask product questions, report problems, or leave feedback.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="mb-10"
          >
            <iframe
              src={`${STATUS_URL}/badge?theme=${prefersLight ? "light" : "dark"}`}
              width="190"
              height="30"
              loading="lazy"
              title="Proxidize Status Page - Opens in a new tab"
              aria-label="Proxidize Status Page - Opens in a new tab"
              className="block border-0"
            />
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <p className="text-muted-foreground mb-1.5 text-sm">
                Questions about our plans, pricing, or request a demo?
              </p>
              <Link
                href={CONTACT_SALES_URL}
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                Talk to Sales
                <ChevronRight size={14} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.28 }}
            >
              <p className="text-muted-foreground mb-1.5 text-sm">
                Browse step-by-step guides, troubleshooting articles, and answers to common
                questions.
              </p>
              <a
                href={HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                Help Center
                <ChevronRight size={14} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.36 }}
            >
              <p className="text-muted-foreground mb-1.5 text-sm">Prefer to reach us directly?</p>
              <a
                href={`mailto:${EMAIL.support}`}
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                {EMAIL.support}
                <ChevronRight size={14} />
              </a>
            </motion.div>
          </div>
        </>
      }
      successContent={null}
      formContent={
        <form onSubmit={formik.handleSubmit} className="space-y-5 p-2 sm:p-8" noValidate>
          <div className="space-y-1">
            <h2 className="text-foreground text-base font-semibold">How can we help?</h2>
            <p className="text-muted-foreground text-xs">
              Please share any relevant information we may need to answer your question.
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="name" className="text-muted-foreground block text-xs font-medium">
              Full name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Jane Smith"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "name") && (
              <FormError>{fieldTouchedError(formik, "name")}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="email" className="text-muted-foreground block text-xs font-medium">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="jane@example.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "email") && (
              <FormError>{fieldTouchedError(formik, "email")}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              id="support-category-label"
              className="text-muted-foreground block text-xs font-medium"
            >
              Category
            </label>
            <div ref={categoryRef} className="relative">
              <button
                type="button"
                id="support-category-trigger"
                aria-haspopup="listbox"
                aria-expanded={categoryOpen}
                aria-controls="support-category-listbox"
                aria-labelledby="support-category-label support-category-trigger"
                onClick={() => setCategoryOpen((o) => !o)}
                onBlur={() => formik.setFieldTouched("category", true)}
                className="border-border bg-background focus:ring-primary/40 flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
              >
                <span className={selectedCategory ? "text-foreground" : "text-muted-foreground"}>
                  {selectedCategory ? selectedCategory.label : "Select a category"}
                </span>
                <ChevronDown
                  size={14}
                  aria-hidden="true"
                  className="text-muted-foreground transition-transform duration-200"
                  style={{ transform: categoryOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>

              {categoryOpen && (
                <ul
                  id="support-category-listbox"
                  role="listbox"
                  aria-labelledby="support-category-label"
                  className="border-border bg-card absolute z-20 mt-1 w-full overflow-hidden rounded-lg border shadow-lg"
                >
                  {SUPPORT_CATEGORIES.map(({ value, label, icon: Icon }) => (
                    <li key={value} role="option" aria-selected={formik.values.category === value}>
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue("category", value);
                          formik.setFieldTouched("category", true);
                          setCategoryOpen(false);
                        }}
                        className="text-foreground hover:bg-muted flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors"
                      >
                        <Icon size={14} className="text-muted-foreground shrink-0" />
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formik.touched.category && formik.errors.category && (
              <FormError>{formik.errors.category}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="message" className="text-muted-foreground block text-xs font-medium">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              placeholder="How do I…"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full resize-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "message") && (
              <FormError>{fieldTouchedError(formik, "message")}</FormError>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className={cn(
                brandActionClasses,
                "inline-flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
              )}
            >
              {formik.isSubmitting ? "Sending…" : "Send message"}
              {!formik.isSubmitting && <Send size={13} />}
            </button>
          </div>
        </form>
      }
    />
  );
}
